import React, { useCallback } from 'react';
import { Dialog, ErrorMessage, Form } from '@koopajs/mui';
import { useResourceCreate, useComponentVisibility } from '@koopajs/react';
import { Typography, Button } from '@mui/material';
import { useLocale } from '@koopajs/react';
import { TrackEvent } from '@koopajs/app';
import { TextField } from 'components/temp/TextFieldTemp';
import { TextMultiLine } from 'components/temp/TextMultiLineTemp';
import { ParticipantsList } from 'components/ParticipantsList';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';

export const SendItemNeedsSignature: React.FC = () => {
  const { t } = useLocale();
  const { triggerSnackbar } = useAppSnackbar();

  const keyPrefix = 'Dialogs.DialogSendItemNeedsSignature';

  const sendReminderDialog = useComponentVisibility('sendToSignReminder');

  const { membersLeftToSign, model, resourceId } = sendReminderDialog.getContext() as {
    membersLeftToSign: string[];
    model: 'minutes' | 'resolution';
    resourceId: string;
  };

  const key = model;

  const { createResource, errorMessage } = useResourceCreate({
    path: `/items-to-sign/${key}/${resourceId}/resend-for-signature`
  });

  const handleSubmit = useCallback(
    async (data: object): Promise<boolean> => {
      const formData = data as { message?: string; reminder?: number; subject?: string };

      if (!formData.message) {
        formData.message = undefined;
      }
      const res = await createResource(data);

      if (res) {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarSendReminderSuccess'),
          variant: 'success'
        });

        TrackEvent('reminder-to-sign-document-sent');
      } else {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarSendReminderFail'),
          variant: 'error'
        });
      }

      return res;
    },
    [createResource]
  );

  const meetingReminderOptions = [0.25, 1, 3, 7].map((option) => {
    let label = 'optionLabelDays';
    let count = option;
    if (option > 0 && option < 1) {
      label = 'optionLabelHours';
      count = option * 24;
    }
    return {
      id: `${option}`,
      label: t(`${keyPrefix}.Form.RadioFields.${label}`, { count })
    };
  });

  return (
    <Dialog.Form
      dialogKey="sendToSignReminder"
      onSubmit={handleSubmit}
      i18n={{
        keyPrefix: `${keyPrefix}.DialogFormSend`,
        options: { documentLabel: `${model}Label` }
      }}
      maxWidth="md"
      leftAction={<Button onClick={sendReminderDialog.setHidden}>{t('common:labelCancel')}</Button>}
      dialogProps={{ className: 'rr-block' }}
    >
      <ErrorMessage error={errorMessage} />

      <Typography sx={{ my: 2 }}>{t(keyPrefix + '.Form.signatoriesLabel')}</Typography>

      <ParticipantsList
        participants={membersLeftToSign?.map((userId) => ({ userId })) || []}
        chipVariant="outlined"
      />

      <TextField
        name="subject"
        validationRules={{ maxLength: 256 }}
        i18n={{ keyPrefix: keyPrefix + '.Form.FieldSubject' }}
      />
      <TextMultiLine
        rows={4}
        name="message"
        validationRules={{ maxLength: 1000 }}
        isOptional={true}
        i18n={{ keyPrefix: keyPrefix + '.Form.FieldMessage' }}
      />

      <Form.Radio
        name="reminder"
        i18n={{ keyPrefix: keyPrefix + '.Form.RadioFields' }}
        options={meetingReminderOptions}
        row
        sx={{ mt: 1, pt: 1 }}
        variant="dense"
        isOptional={true}
      />
    </Dialog.Form>
  );
};
